import { useTranslation } from 'react-i18next';

/**
 * Qué se puede hacer con un documento, en dos iconos.
 *
 * Existe para el listado: con 49 normativos, saber quién se puede descargar
 * obligaba a abrir ficha por ficha.
 *
 * **No depende del color.** Un icono tachado es «no permitido» aunque se vea en
 * blanco y negro, y la gotita —tachada o no— dice si la copia sale con marca de
 * agua. El color sólo refuerza: ámbar en lo que sale sin marca, porque es lo
 * único de aquí que quita trazabilidad.
 */
function Permiso({
  activo,
  conMarca,
  titulo,
  children,
}: {
  activo: boolean;
  conMarca: boolean;
  titulo: string;
  children: React.ReactNode;
}) {
  const tono = !activo ? 'no' : conMarca ? 'si' : 'sinmarca';
  return (
    <span className={`permiso permiso--${tono}`} title={titulo} aria-label={titulo} role="img">
      <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5">
        {children}
        {/* Tachado: la diferencia se ve sin color. */}
        {!activo && <path d="M3 13 13 3" strokeLinecap="round" />}
      </svg>
      {activo && (
        <svg className="permiso__gota" viewBox="0 0 10 10" width="8" height="8" aria-hidden="true">
          <path
            d="M5 1c1.6 2 2.6 3.2 2.6 4.4A2.6 2.6 0 0 1 5 8a2.6 2.6 0 0 1-2.6-2.6C2.4 4.2 3.4 3 5 1Z"
            fill={conMarca ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="1"
          />
          {!conMarca && <path d="M1.5 8.5 8.5 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />}
        </svg>
      )}
    </span>
  );
}

export function PermisosDocumento({
  isPrintable,
  printWatermark,
  isDownloadable,
  downloadWatermark,
}: {
  isPrintable: boolean;
  printWatermark: boolean;
  isDownloadable: boolean;
  downloadWatermark: boolean;
}) {
  const { t } = useTranslation();
  const texto = (accion: string, permitido: boolean, marca: boolean) =>
    `${accion}: ${
      !permitido
        ? t('documents.notAllowed')
        : marca
          ? t('documents.withWatermark')
          : t('documents.withoutWatermark')
    }`;

  return (
    <span className="permisos">
      <Permiso
        activo={isPrintable}
        conMarca={printWatermark}
        titulo={texto(t('documents.printable'), isPrintable, printWatermark)}
      >
        {/* Impresora */}
        <path d="M4.5 6V2.5h7V6" strokeLinejoin="round" />
        <path d="M2.5 6h11v5h-2v2.5h-7V11h-2z" strokeLinejoin="round" />
      </Permiso>
      <Permiso
        activo={isDownloadable}
        conMarca={downloadWatermark}
        titulo={texto(t('documents.downloadable'), isDownloadable, downloadWatermark)}
      >
        {/* Descarga */}
        <path d="M8 2.5v7.5m0 0L5 7m3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2.8 11.5v1.2h10.4v-1.2" strokeLinecap="round" />
      </Permiso>
    </span>
  );
}
